/**
 * FollowButton — Toggle follow/unfollow for a user.
 */
import { useState, useEffect } from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, SPACE } from '@/constants/designTokens';
import { followUser, unfollowUser, isFollowing } from '@/lib/followSystem';

interface FollowButtonProps {
  targetUserId: string;
  /** Compact variant for leaderboard rows */
  compact?: boolean;
}

export default function FollowButton({ targetUserId, compact = false }: FollowButtonProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (!profile?.id || profile.id === targetUserId) { setLoading(false); return; }
    isFollowing(profile.id, targetUserId).then(f => {
      setFollowing(f);
      setLoading(false);
    });
  }, [profile?.id, targetUserId]);

  if (!profile?.id || profile.id === targetUserId || loading) return null;

  const toggle = async () => {
    setToggling(true);
    if (following) {
      const ok = await unfollowUser(profile.id, targetUserId);
      if (ok) setFollowing(false);
    } else {
      const ok = await followUser(profile.id, targetUserId);
      if (ok) setFollowing(true);
    }
    setToggling(false);
  };

  return (
    <TouchableOpacity
      style={[
        compact ? styles.compactBtn : styles.btn,
        following
          ? { borderColor: c.border, backgroundColor: 'transparent' }
          : { borderColor: c.accent, backgroundColor: c.accent },
      ]}
      onPress={toggle}
      disabled={toggling}
      activeOpacity={0.7}
    >
      {toggling ? (
        <ActivityIndicator size="small" color={following ? c.textSecondary : '#000'} />
      ) : (
        <Text style={[
          compact ? styles.compactText : styles.text,
          { color: following ? c.textSecondary : '#000', fontFamily: TYPO.body.semibold },
        ]}>
          {following ? 'FOLLOWING' : 'FOLLOW'}
        </Text>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  btn: { paddingVertical: 8, paddingHorizontal: 16, borderRadius: RADIUS.xs, borderWidth: 1, alignItems: 'center', minWidth: 96 },
  text: { fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' as const, flexShrink: 0 },
  compactBtn: { paddingVertical: 4, paddingHorizontal: 10, borderRadius: RADIUS.xs, borderWidth: 1, alignItems: 'center', minWidth: 80 },
  compactText: { fontSize: 11, letterSpacing: 0.3, textTransform: 'uppercase' as const, flexShrink: 0 },
});
